import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang, useSeo } from "../i18n/LangContext";
import { books } from "../i18n/content";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { QuitandaSeal } from "../components/site/Mark";
import { BookCover } from "../components/site/BookCover";
import { FinalCta } from "../components/site/Sections";
import { STORE_URL } from "../config";

const StoreBtn = ({ label, testId, light }) => (
  <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className={light ? "btn-light" : "btn-dark"} data-testid={testId}>
    {label} <ArrowUpRight size={18} />
  </a>
);

export default function Quitanda() {
  const { t } = useLang();
  useSeo("quitanda");
  const q = t.quitandaPage;
  return (
    <>
      <section className="mx-auto max-w-[1400px] px-6 pb-20 pt-36 sm:px-10 lg:pt-48" data-testid="quitanda-header">
        <Reveal><Eyebrow>{q.eyebrow}</Eyebrow></Reveal>
        <Reveal delay={0.1} className="mt-10"><QuitandaSeal sig={t.quitandaBlock.sig} size="lg" /></Reveal>
        <div className="mt-12 grid gap-10 lg:grid-cols-12">
          <Reveal delay={0.2} className="lg:col-span-7">
            <p className="f-serif text-[clamp(1.6rem,3vw,2.6rem)] leading-[1.18] tracking-[-0.01em]">{q.positioning}</p>
          </Reveal>
          <Reveal delay={0.3} className="lg:col-span-4 lg:col-start-9 lg:self-end">
            <StoreBtn label={q.store} testId="quitanda-store-btn-top" />
          </Reveal>
        </div>
      </section>

      <section className="border-t border-[#121518]" data-testid="quitanda-credentials">
        <div className="mx-auto grid max-w-[1400px] gap-10 px-6 py-16 sm:px-10 lg:grid-cols-12 lg:py-24">
          <Reveal className="lg:col-span-4"><h2 className="f-serif text-3xl lg:text-4xl">{q.credsTitle}</h2></Reveal>
          <ol className="lg:col-span-8">
            {q.creds.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <li className="flex items-baseline gap-6 border-b border-[#E2DDD5] py-6">
                  <span className="f-serif w-8 shrink-0 italic text-[#A3401A]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-lg leading-snug lg:text-xl">{c}</span>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="bg-[#F3EFEA]" data-testid="quitanda-books">
        <div className="mx-auto max-w-[1400px] px-6 py-20 sm:px-10 lg:py-32">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <Reveal><h2 className="f-serif text-[clamp(2.2rem,5vw,4.5rem)] leading-none tracking-[-0.02em]">{q.booksTitle}</h2></Reveal>
            <Reveal delay={0.1}><p className="text-xs text-[#4A525A]">{q.booksNote}</p></Reveal>
          </div>
          <div className="mt-14 grid grid-cols-2 gap-x-5 gap-y-12 sm:grid-cols-3 lg:grid-cols-4 lg:gap-x-10">
            {books.map((b, i) => (
              <Reveal key={b.title} delay={(i % 4) * 0.08} y={40}>
                <BookCover title={b.title} index={i} box={b.box} boxLabel={q.box} testId={`book-cover-${i}`} />
              </Reveal>
            ))}
            <Reveal delay={0.3} className="flex aspect-[2/3] flex-col justify-end border border-[#121518] p-6">
              <p className="f-serif text-xl leading-tight">+150</p>
              <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="link-u mt-3 w-fit text-sm" data-testid="books-store-link">{q.store}</a>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="autores" className="scroll-mt-24 mx-auto max-w-[1400px] px-6 py-20 sm:px-10 lg:py-32" data-testid="quitanda-authors">
        <div className="grid gap-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <h2 className="f-serif text-[clamp(2.4rem,5vw,4.5rem)] leading-none tracking-[-0.02em]">{q.authorsTitle}</h2>
            <p className="mt-6 max-w-sm text-base leading-relaxed text-[#2E353B]">{q.authorsIntro}</p>
          </Reveal>
          <div className="grid gap-5 sm:grid-cols-3 lg:col-span-7">
            {q.authors.map((a, i) => (
              <Reveal key={a.t} delay={i * 0.08} className="h-full">
                <div className="flex h-full flex-col border-t-2 border-[#FF682F] bg-white p-6">
                  <span className="f-serif text-sm italic text-[#4A525A]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="f-serif mt-8 text-xl leading-tight">{a.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#2E353B]">{a.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.2} className="mt-12 flex flex-wrap gap-4 lg:ml-[41.66%]">
          <Link to="/contacto?interesse=autor" className="btn-dark" data-testid="authors-talk-btn">{q.authorsCta} <ArrowRight size={16} /></Link>
        </Reveal>
      </section>

      <section className="bg-[#0E1A1B] text-[#FAF8F5]" data-testid="quitanda-institutions">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-6 py-20 sm:px-10 lg:grid-cols-12 lg:py-24">
          <Reveal className="lg:col-span-4"><h2 className="f-serif text-3xl lg:text-4xl">{q.instTitle}</h2></Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
            <p className="text-lg leading-relaxed text-[#E9E4DA]">{q.instText}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/o-que-fazemos#publicar" className="btn-light" data-testid="quitanda-inst-link">{q.instLink} <ArrowRight size={16} /></Link>
              <a href={STORE_URL} target="_blank" rel="noopener noreferrer" className="link-u self-center text-sm" data-testid="quitanda-store-btn-bottom">{q.store}</a>
            </div>
          </Reveal>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
